import { router, Stack, useLocalSearchParams } from "expo-router";
import { useEffect, useRef, useState } from "react";
import { ActivityIndicator, Linking, Pressable, ScrollView, Text, View } from "react-native";

import { PublicBuylistCardRow } from "@/components/buylist/PublicBuylistCardRow";
import { PublicBuylistRules } from "@/components/buylist/PublicBuylistRules";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { Input } from "@/components/ui/Input";
import { Screen } from "@/components/ui/Screen";
import { EVENTS_BASE_URL } from "@/config";
import { useMarkInteractive } from "@/hooks/useMarkInteractive";
import { BuylistUnavailable, usePublicBuylist } from "@/hooks/usePublicBuylist";
import { usePublicShow } from "@/hooks/usePublicShow";
import { loadShopAnalytics } from "@/lib/browse/shopAnalyticsClient";
import { shopPath } from "@/lib/shop/publicUrls";

export default function PublicBuylistScreen() {
  const { slug, show, source } = useLocalSearchParams<{
    slug: string;
    show?: string;
    source?: string;
  }>();
  const [search, setSearch] = useState("");
  const [game, setGame] = useState("");
  const [setName, setSetName] = useState("");
  const [sort, setSort] = useState<"cash" | "name" | "set">("cash");
  const buylist = usePublicBuylist(slug ?? null, search, game || null, setName || null, sort);
  const linkedShow = usePublicShow(show);
  useMarkInteractive(!buylist.isPending);
  const page = buylist.data?.pages[0];
  const tracked = useRef(false);
  useEffect(() => {
    if (!page || tracked.current) return;
    tracked.current = true;
    void loadShopAnalytics().then((analytics) =>
      analytics.buylistViewed(page.shop_slug, show ? "show" : source === "qr" ? "qr" : "direct")
    );
  }, [page, show, source]);
  const cards = buylist.data?.pages.flatMap((part) => part.cards) ?? [];
  const hot = cards.filter((card) => card.hot);
  const regular = cards.filter((card) => !card.hot);
  const toShop = () => router.push(shopPath(slug));

  if (buylist.isPending) {
    return (
      <Screen role="main">
        <ActivityIndicator accessibilityLabel="Loading buylist" />
      </Screen>
    );
  }
  if (buylist.error || !page) {
    const name = buylist.error instanceof BuylistUnavailable ? buylist.error.shopName : slug;
    return (
      <Screen role="main">
        <View className="mx-auto w-full max-w-[840px] flex-1 justify-center px-4">
          <EmptyState
            title={`${name} isn't posting a buylist right now`}
            subtitle="The shop page is still available."
            mark
          />
          <Button title={`Visit ${name}'s shop`} onPress={toShop} />
        </View>
      </Screen>
    );
  }

  return (
    <Screen role="main">
      <Stack.Screen options={{ title: `${page.shop_name} is buying` }} />
      <ScrollView
        className="flex-1"
        contentContainerClassName="mx-auto w-full max-w-[840px] gap-5 px-4 py-8 pb-24"
        keyboardShouldPersistTaps="handled"
      >
        <Pressable accessibilityRole="link" onPress={toShop} className="min-h-11 justify-center">
          <Text className="font-label text-sm text-primary">← {page.shop_name}</Text>
        </Pressable>
        {show
          ? (
            <Pressable
              accessibilityRole="link"
              onPress={() =>
                void Linking.openURL(`${EVENTS_BASE_URL}/shows/${encodeURIComponent(show)}`)}
              className="min-h-11 justify-center"
            >
              <Text className="font-label text-sm text-primary">
                ← {linkedShow.show?.name ?? "Show"} vendors
              </Text>
            </Pressable>
          )
          : null}
        <View className="gap-2">
          <Text
            accessibilityRole="header"
            aria-level={1}
            className="font-title text-3xl text-foreground"
          >
            We're buying
          </Text>
          {page.public_note
            ? <Text className="font-body text-base text-foreground">{page.public_note}</Text>
            : null}
          <Text className="font-body text-sm text-muted-foreground">
            Near Mint prices{page.as_of ? ` as of ${new Date(page.as_of).toLocaleString()}` : ""}.
            Final price confirmed at the table.
          </Text>
        </View>
        <View className="gap-3">
          <Input
            value={search}
            onChangeText={setSearch}
            placeholder="Search cards"
            accessibilityLabel="Search buylist cards"
          />
          <View className="flex-row gap-2">
            <View className="min-w-0 flex-1">
              <Input
                value={game}
                onChangeText={setGame}
                placeholder="Game"
                accessibilityLabel="Filter by game"
              />
            </View>
            <View className="min-w-0 flex-1">
              <Input
                value={setName}
                onChangeText={setSetName}
                placeholder="Set"
                accessibilityLabel="Filter by set"
              />
            </View>
          </View>
          <View className="flex-row gap-2">
            {(["cash", "name", "set"] as const).map((option) => (
              <Button
                key={option}
                title={{ cash: "Highest cash", name: "A–Z", set: "Set" }[option]}
                size="sm"
                variant={sort === option ? "primary" : "outline"}
                onPress={() => setSort(option)}
              />
            ))}
          </View>
        </View>
        {cards.length === 0 && page.rules.length === 0
          ? <EmptyState title="No matching cards" subtitle="Try another search or filter." />
          : null}
        <PublicBuylistRules rules={page.rules} />
        {hot.length > 0
          ? (
            <View>
              <Text accessibilityRole="header" className="font-title text-xl text-foreground">
                Most wanted
              </Text>
              {hot.map((card) => <PublicBuylistCardRow key={card.public_id} card={card} />)}
            </View>
          )
          : null}
        {regular.length > 0
          ? (
            <View>
              <Text accessibilityRole="header" className="font-title text-xl text-foreground">
                All cards
              </Text>
              {regular.map((card) => <PublicBuylistCardRow key={card.public_id} card={card} />)}
            </View>
          )
          : null}
        {buylist.hasNextPage
          ? (
            <Button
              title={buylist.isFetchingNextPage ? "Loading…" : "Load more cards"}
              variant="outline"
              disabled={buylist.isFetchingNextPage}
              onPress={() => void buylist.fetchNextPage()}
            />
          )
          : null}
      </ScrollView>
    </Screen>
  );
}
